import React from 'react';
import {
  Grid,
  GridNoRecords,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { useGetEventReportCompetencies } from '../../../../hooks/services/use-reports';

const initialDataState: State = {
  sort: [{ field: 'participant', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export const ParticipantCompetenciesReportTab: React.FC = () => {
  const { eventId, detailId } = useParams();

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const { data, isLoading } = useGetEventReportCompetencies({
    type: 'participants',
    eventId,
    playlistId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });
  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const ratingColumn = (props: GridCellProps) => {
    return <td className={'k-table-td'}>{props.dataItem.rating.toFixed(1)}</td>;
  };

  return (
    <div
      className={'pt-2'}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
      <GridToolBar
        searchPlaceholder={'Search competency'}
        columnsToSearch={['participant', 'team', 'competency', 'description']}
        exportRef={gridExportRef}
        showGridMode={false}
        showCardMode={false}
        showBulkExport={true}
        {...dataState}
        onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}></GridToolBar>
      <ExcelExport
        data={data?.data ?? []}
        ref={gridExportRef}
        fileName={`competency-rating.xlsx`}>
        <Grid
          id={'grid-participants'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={data}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'participant'}
            title={'Participant'}
            width={150}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'team'}
            title={'Team'}
            width={150}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'competency'}
            title={'Competency'}
            width={180}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'description'}
            title={'Description'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'rating'}
            title={'Rating'}
            width={150}
            columnMenu={ColumnMenu}
            cells={{ data: (props) => ratingColumn(props) }}
          />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </ExcelExport>
    </div>
  );
};
